import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClaimDetailed } from "@/api/generated/client/models";
import {
    ShareClaimRequestOneOf2TypeEnum,
    RespondToAppraisalRequestOneOfOutcomeEnum,
    RespondToAppraisalRequestOneOf1OutcomeEnum,
} from "@/api/generated/client/models";
import type { RespondToAppraisalRequest } from "@/api/generated/client/models";
import { appraiserConfig } from "@/config/appraiser";
import { apiClient } from "@/lib/api-client";
import { ApiError, createApiError } from "@/lib/errors";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";

interface UseAppraisalSchedulerOptions {
    claim: ClaimDetailed | null | undefined;
    userName?: string | null;
    deductibleCents?: number | null;
    open: boolean;
}

export function useAppraisalScheduler({
    claim,
    userName,
    deductibleCents,
    open,
}: UseAppraisalSchedulerOptions) {
    const [hasScheduled, setHasScheduled] = useState(false);
    const requiresScheduling = Boolean(appraiserConfig.calendlyEmbedUrl);

    const calendlyUrl = useMemo(() => {
        if (!appraiserConfig.calendlyEmbedUrl) return "";

        const url = new URL(appraiserConfig.calendlyEmbedUrl);
        const params = url.searchParams;
        params.set("hide_gdpr_banner", "1");
        params.set("embed_type", "Inline");
        params.set("embed_domain", typeof window !== "undefined" ? window.location.host : "");
        const addParam = (key: string, value: string | null | undefined) => {
            if (value?.trim()) params.set(key, value.trim());
        };

        addParam("name", claim?.insuredName);
        addParam("email", claim?.insuredEmail);
        addParam("a1", claim?.insuranceCarrier);
        addParam("a2", userName);
        addParam("a4", "Appraisal");
        addParam("a5", claim?.contractor);
        addParam("a6", claim?.propertyAddress);
        addParam("a7", claim?.policyNumber);
        addParam("a8", claim?.claimNumber);
        addParam("a9", claim?.dateOfLoss);
        const deductible = deductibleCents != null
            ? `$${(deductibleCents / 100).toFixed(2)}`
            : undefined;
        addParam("a10", [claim?.lossType, deductible].filter(Boolean).join(", "));

        return url.toString();
    }, [claim, deductibleCents, userName]);

    useEffect(() => {
        if (!open || !requiresScheduling) return;

        const handleMessage = (event: MessageEvent) => {
            if (event.origin !== "https://calendly.com") return;
            if (event.data?.event === "calendly.event_scheduled") {
                setHasScheduled(true);
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [open, requiresScheduling]);

    return {
        calendlyUrl,
        requiresScheduling,
        hasScheduled,
        resetScheduling: () => setHasScheduled(false),
    };
}

async function parseAppraisalError(error: unknown): Promise<ApiError> {
    if (
        error !== null &&
        typeof error === "object" &&
        "response" in error &&
        typeof globalThis.Response !== "undefined" &&
        (error as { response: unknown }).response instanceof Response
    ) {
        const response = (error as { response: Response }).response;
        try {
            const body = await response.clone().json() as Record<string, unknown>;
            const details = body.error && typeof body.error === "object"
                ? body.error as Record<string, unknown>
                : body;
            if (typeof details.message === "string" || typeof details.code === "string") {
                return new ApiError(
                    typeof details.message === "string" ? details.message : "An unexpected error occurred",
                    response.status,
                    typeof details.code === "string" ? details.code : undefined,
                    typeof details.details === "object" && details.details !== null
                        ? details.details as Record<string, unknown>
                        : undefined,
                    typeof details.correlationId === "string" ? details.correlationId : undefined,
                );
            }
        } catch {
            // Fall back to the synchronous parser when the response is not JSON.
        }
    }

    return createApiError(error);
}

export function useAppraisalRequest(claimId: number) {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async () => {
            try {
                return await apiClient.claims.shareClaim({
                    claimId,
                    shareClaimRequest: {
                        type: ShareClaimRequestOneOf2TypeEnum.Email,
                        email: appraiserConfig.email,
                        level: "rw",
                        isAppraisalRequest: true,
                    },
                });
            } catch (error) {
                throw await parseAppraisalError(error);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["claims", claimId] });
            queryClient.invalidateQueries({ queryKey: ["claims", claimId, "assignable-users"] });
            queryClient.invalidateQueries({ queryKey: ["claim-activity", claimId] });
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
            toast({
                title: `Sent to ${appraiserConfig.name}`,
                description: `${appraiserConfig.name} now has access to this claim.`,
            });
        },
        onError: (error: unknown) => {
            const apiError = createApiError(error);
            toast({
                title: "Failed to request appraisal",
                description: apiError.message || `There was an error sharing this claim with ${appraiserConfig.name}.`,
                variant: "destructive",
            });
        },
    });

    return {
        requestAppraisal: (options?: { onSuccess?: () => void }) =>
            mutation.mutate(undefined, { onSuccess: options?.onSuccess }),
        isRequestingAppraisal: mutation.isPending,
    };
}

type AppraisalOutcome =
    | typeof RespondToAppraisalRequestOneOfOutcomeEnum[keyof typeof RespondToAppraisalRequestOneOfOutcomeEnum]
    | typeof RespondToAppraisalRequestOneOf1OutcomeEnum[keyof typeof RespondToAppraisalRequestOneOf1OutcomeEnum];

export interface AppraisalResponseInput {
    outcome: AppraisalOutcome;
    requestEventId: number;
    notes?: string;
}

export function useAppraisalResponse(claimId: number, requestEventId: number) {
    const { user } = useAuth();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [selectedOutcome, setSelectedOutcome] = useState<AppraisalResponseInput["outcome"] | null>(null);
    const [notes, setNotes] = useState("");

    const mutation = useMutation({
        mutationFn: async (input: AppraisalResponseInput) => {
            const respondToAppraisalRequest: RespondToAppraisalRequest = input.outcome === "accepted"
                ? { outcome: input.outcome, requestEventId: input.requestEventId, notes: input.notes }
                : { outcome: input.outcome, requestEventId: input.requestEventId, notes: input.notes ?? "" };
            try {
                return await apiClient.claims.respondToAppraisal({
                    claimId,
                    respondToAppraisalRequest,
                });
            } catch (error) {
                throw await parseAppraisalError(error);
            }
        },
        onSuccess: (result) => {
            setSelectedOutcome(null);
            setNotes("");
            queryClient.invalidateQueries({ queryKey: ["claim-activity", claimId] });
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
            toast({
                title: "Appraisal response sent",
                description: `Marked as "${result?.data?.outcomeLabel ?? "updated"}".`,
            });
        },
        onError: (error: unknown) => {
            const apiError = createApiError(error);
            toast({
                title: "Failed to submit appraisal response",
                description: apiError.message || "There was an error submitting your appraisal response.",
                variant: "destructive",
            });
        },
    });

    const outcome = appraiserConfig.appraisalOutcomes.find((item) => item.value === selectedOutcome);
    const notesRequired = Boolean(outcome && outcome.value !== "accepted");
    const canSubmit = Boolean(outcome && (!notesRequired || notes.trim()));
    const isAppraiser = user?.email?.toLowerCase() === appraiserConfig.email.toLowerCase();

    const handleSubmit = () => {
        if (!outcome || !canSubmit) return;

        mutation.mutate(
            { outcome: outcome.value, requestEventId, notes: notes.trim() || undefined },
        );
    };

    return {
        submitAppraisalResponse: (
            input: Omit<AppraisalResponseInput, "requestEventId">,
            options?: { onSuccess?: () => void },
        ) => mutation.mutate({ ...input, requestEventId }, { onSuccess: options?.onSuccess }),
        isSubmittingAppraisalResponse: mutation.isPending,
        isAppraiser,
        appraisalOutcomes: appraiserConfig.appraisalOutcomes,
        selectedOutcome,
        setSelectedOutcome,
        notes,
        setNotes,
        outcome,
        canSubmit,
        handleSubmit,
    };
}